import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { useNavigate, useParams } from "react-router-dom";
import { MultiSelect, ScrollArea, TextInput } from "../../../../components";
import {
  getRequest,
  optimisticOptions,
  postRequest,
  putRequest,
  queries,
} from "../../../../react-query";
import { useMutation, useQuery } from "react-query";
import { validateForm } from "../../../../utils";
import errorToast from "../../../../utils/errorToast";
import { JsonData, JsonEditor, githubLightTheme } from "json-edit-react";
import { FormData } from "./camera.interface";

export const UpdateFormData = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState<FormData>({
    cameras: [],
    form_payload: {},
    form_mappings: {},
    search: "",
    end_point: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: arrestoFormData = {}, isLoading: isFormDataLoading }: any =
    useQuery(
      [queries.users, id],
      () => getRequest(`/arresto-form/${id}`),
      {
        enabled: !!id,
      }
    );
  // Destructuring Data
  const { data: arrestoForm = {} } = arrestoFormData;

  const validationRules = {
    cameras: [{ required: true, message: "Select atleast one camera!." }],
    end_point: [{ required: true, message: "Required Field!." }],
  };

  const { mutate: updateArrestoForm } = useMutation(
    putRequest,
    optimisticOptions(queries.users)
  );

  const { data: cameras_data = {}, isLoading: isFetchingCameras } = useQuery(
    [queries.camera],
    () => getRequest(`/camera`),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );
  const { data: cameras = [] } = cameras_data;

  const onSubmit = useCallback(
    (e: any) => {
      e.preventDefault();

      // Validate form data
      const formErrors = validateForm(form, validationRules);
      setErrors(formErrors);

      // If no validation errors, proceed with submission
      if (Object.keys(formErrors).length === 0) {
        const { cameras, form_payload, form_mappings, end_point } = form;
        updateArrestoForm({
          endPoint: `/arresto-form/${id}`,
          payload: {
            camera_ids:
              Array.isArray(cameras) &&
              form.cameras?.map((ele: any) => {
                return ele.value;
              }),
            end_point: end_point,
            form_payload: form_payload,
            form_mappings: form_mappings,
          },
        });
        navigate(-1);
      } else {
        errorToast("please check all fields");
      }
    },
    [form, id]
  );

  const onChangeHandler = (
    value: string[] | JsonData | string,
    name: string
  ) => {
    setForm({
      ...form,
      [name]: value,
    });
  };

  useEffect(() => {
    if (arrestoForm.id) {
      const { camera_details, form_payload, form_mappings, end_point } =
        arrestoForm;
      setForm({
        ...form,
        end_point: end_point,
        form_payload: form_payload,
        form_mappings: form_mappings,
        cameras: Array.isArray(camera_details)
          ? camera_details?.map((ele) => ({
              label: ele.name,
              value: ele.id,
            }))
          : [],
      });
    }
  }, [setForm, arrestoForm]);

  if (isFormDataLoading) {
    return <div />;
  }

  return (
    <form className="bg-white w-full h-full p-8 space-y-4" onSubmit={onSubmit}>
      <div className="w-full flex justify-between items-center space-x-2">
        <div className="z-[9999]">
          <MultiSelect
            theme="black"
            isMulti={true}
            keyField="id"
            valueField="name"
            data={cameras}
            onValueChange={(value) => onChangeHandler(value, "cameras")}
            value={form.cameras}
            placeholder="Select Cameras"
            error={errors.cameras}
          >
            <label htmlFor="cameras">Cameras*</label>
          </MultiSelect>
        </div>
        <div>
          <input
            type="text"
            placeholder="Search in JSON..."
            value={form.search}
            onChange={(e: any) => onChangeHandler(e.target.value, "search")}
            className="border border-gray-500 px-1"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <ScrollArea className="h-[calc(100vh-280px)]">
          <div className="w-full h-full flex justify-between  space-x-4">
            <JsonEditor
              data={form.form_payload}
              setData={(data: JsonData) =>
                onChangeHandler(data, "form_payload")
              }
              rootName="Payload Json"
              enableClipboard={true}
              className="w-full"
              theme={githubLightTheme}
              searchText={form.search}
            />
            <div className="h-[calc(100vh-280px)] w-[0.75px] border border-separate border-dash" />
            <JsonEditor
              data={form.form_mappings}
              setData={(data: JsonData) =>
                onChangeHandler(data, "form_mappings")
              }
              rootName="Field Relation Mapping Json"
              enableClipboard={true}
              className="w-full"
              theme={githubLightTheme}
              searchText={form.search}
            />
          </div>
        </ScrollArea>
      </div>

      <div className="w-full flex justify-between items-center space-x-6">
        <TextInput
          type="text"
          placeholder=""
          value={form.end_point}
          onChange={(e) => onChangeHandler(e.target.value, "end_point")}
          theme="black"
          error={errors.end_point}
        >
          <label htmlFor="name">Api URL</label>
        </TextInput>
        <button
          type="submit"
          className="bg-green px-6 py-2 rounded-md text-white"
        >
          Submit
        </button>
      </div>
    </form>
  );
};
